import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, Users } from 'lucide-react';
import { Avatar } from '@clareefai/ui';
import KanbanBoard from '../shared/KanbanBoard';
import { useGetProspectsQuery } from '../../store/prospectApi';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { ProspectResponse } from '../../store/prospectApi';

// Map backend status to frontend kanban column status
const mapStatusToKanbanColumn = (status: string): string => {
  const statusMap: Record<string, string> = {
    'NEW': 'introduce',
    'CONTACTED': 'request_sent',
    'QUALIFIED': 'scheduled',
    'PROPOSAL': 'scheduled',
    'NEGOTIATION': 'scheduled',
    'WON': 'completed',
    'LOST': 'failed',
  };
  return statusMap[status] || 'introduce';
};

// Convert backend ProspectResponse to frontend Prospect format
interface ProspectCard {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  assignedPromoters?: string[];
  status: string;
  createdDate: string;
  sectors?: string[];
  companySize?: string | null;
}

const ProspectsKanban: React.FC = () => {
  const { t } = useTranslation();
  const { data: prospectsData, isLoading, error } = useGetProspectsQuery();
  const { data: promotersData } = useGetPromotersQuery();
  const navigate = useNavigate();

  // Convert backend data to frontend format
  const prospects: ProspectCard[] = useMemo(() => {
    if (!prospectsData) return [];
    return prospectsData.map((prospect) => ({
      id: prospect.id || '',
      name: `${prospect.firstName} ${prospect.lastName}`,
      email: prospect.email,
      phone: prospect.phone || '',
      company: prospect.company || '',
      assignedPromoters: prospect.assignedPromoters,
      status: mapStatusToKanbanColumn(prospect.status),
      createdDate: prospect.createdAt || new Date().toISOString(),
      sectors: prospect.sectors,
      companySize: prospect.companySize,
    }));
  }, [prospectsData]);

  const promoters = useMemo(() => {
    if (!promotersData) return [];
    return promotersData.map((promoter) => ({
      id: promoter.id,
      name: `${promoter.firstName} ${promoter.lastName}`,
      email: promoter.email,
      phone: promoter.phone || '',
      company: promoter.company || '',
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(promoter.firstName + ' ' + promoter.lastName)}&background=4339A0&color=fff`,
      status: 'available' as const,
      prospectsReferred: 0,
      conversionRate: 0,
      totalRewards: 0,
      joinedDate: promoter.createdAt,
    }));
  }, [promotersData]);

  const columns = [
    {
      id: 'introduce',
      title: t('prospects.introduce'),
      items: prospects.filter(p => p.status === 'introduce'),
      color: 'bg-yellow-100 text-yellow-800',
      headerColor: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      borderColor: '#eab308'
    },
    {
      id: 'request_sent',
      title: t('prospects.requestSent'),
      items: prospects.filter(p => p.status === 'request_sent'),
      color: 'bg-blue-100 text-blue-800',
      headerColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      borderColor: '#3b82f6'
    },
    {
      id: 'scheduled',
      title: t('prospects.scheduled'),
      items: prospects.filter(p => p.status === 'scheduled'),
      color: 'bg-green-100 text-green-800',
      headerColor: 'bg-gradient-to-r from-green-500 to-emerald-500',
      borderColor: '#10b981'
    },
    {
      id: 'completed',
      title: t('prospects.completed'),
      items: prospects.filter(p => p.status === 'completed'),
      color: 'bg-purple-100 text-purple-800',
      headerColor: 'bg-gradient-to-r from-purple-500 to-violet-500',
      borderColor: '#8b5cf6'
    },
    {
      id: 'failed',
      title: t('prospects.failed'),
      items: prospects.filter(p => p.status === 'failed'),
      color: 'bg-red-100 text-red-800',
      headerColor: 'bg-gradient-to-r from-red-500 to-pink-500',
      borderColor: '#ef4444'
    }
  ];

  const handleProspectClick = (prospectId: string) => {
    navigate(`/prospect/${prospectId}`);
  };

  const getAssociatedPromoters = (prospect: ProspectCard) => {
    if (!prospect.assignedPromoters || prospect.assignedPromoters.length === 0) return [];
    return promoters.filter(p => 
      prospect.assignedPromoters?.includes(p.id)
    );
  };

  const renderCard = (prospect: ProspectCard) => (
    <div
      className="space-y-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
      onClick={() => handleProspectClick(prospect.id)}
    >
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{prospect.name}</h4>
      </div>
      
      <div className="text-sm text-gray-600 space-y-1">
        <p className="flex items-center">
          <Building className="w-4 h-4 mr-2" />
          {prospect.company}
        </p>
      </div>

      {/* Promoters avatars */}
      <div className="flex items-center justify-between">
        <div className="flex -space-x-1">
          {getAssociatedPromoters(prospect).slice(0, 3).map((promoter) => (
            <Avatar
              key={promoter.id}
              src={promoter.avatar}
              name={promoter.name}
              size="xs"
              className="border-2 border-white shadow-sm"
            />
          ))}
          {getAssociatedPromoters(prospect).length > 3 && (
            <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center shadow-sm">
              <span className="text-xs text-gray-600 font-medium">
                +{getAssociatedPromoters(prospect).length - 3}
              </span>
            </div>
          )}
          {getAssociatedPromoters(prospect).length === 0 && (
            <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
              <Users className="w-3 h-3 text-gray-400" />
            </div>
          )}
        </div>
        <span className="text-xs text-gray-500">
          {getAssociatedPromoters(prospect).length} {getAssociatedPromoters(prospect).length !== 1 ? t('prospects.kanbanCard.promoters') : t('prospects.kanbanCard.promoter')}
        </span>
      </div>

      <div className="pt-2 border-t">
        <div className="text-xs text-gray-500 mb-2">
          {t('prospects.kanbanCard.createdOn')} {new Date(prospect.createdDate).toLocaleDateString('en-US')}
        </div>
        <span className={`status-badge ${
          prospect.status === 'introduce' ? 'status-invited' :
          prospect.status === 'request_sent' ? 'status-review' :
          prospect.status === 'scheduled' ? 'status-available' :
          prospect.status === 'completed' ? 'status-completed' :
          'status-failed'
        }`}>
          {prospect.status === 'introduce' ? t('prospects.kanbanCard.toIntroduce') :
           prospect.status === 'request_sent' ? t('prospects.kanbanCard.requestSent') :
           prospect.status === 'scheduled' ? t('prospects.kanbanCard.scheduled') :
           prospect.status === 'completed' ? t('prospects.kanbanCard.completed') :
           t('prospects.kanbanCard.failed')}
        </span>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">{t('prospects.kanbanCard.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">{t('prospects.kanbanCard.errorLoading')}</div>
      </div>
    );
  }

  return (
    <KanbanBoard
      columns={columns}
      renderCard={renderCard}
    />
  );
};

export default ProspectsKanban;
